/**
 * Pan and zoom (requirement 12.8).
 *
 * The viewport is the scene point at the canvas's top-left and a scale. It is
 * view state: nothing here reads or writes the design document, which is how
 * "zoom does not alter stored coordinates" holds by construction rather than
 * by care. Pointer positions arrive in screen pixels relative to the canvas
 * and are converted to scene units here before anything else sees them.
 */
import type { Viewport } from "../render";
import type { Bounds, Point } from "../scene/types";

export const MIN_ZOOM = 0.25;
export const MAX_ZOOM = 4;

/** Each zoom step multiplies or divides by this. */
export const ZOOM_STEP = 1.2;

export function clampZoom(zoom: number): number {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));
}

export function screenToScene(viewport: Viewport, point: Point): Point {
  return { x: viewport.x + point.x / viewport.zoom, y: viewport.y + point.y / viewport.zoom };
}

export function sceneToScreen(viewport: Viewport, point: Point): Point {
  return { x: (point.x - viewport.x) * viewport.zoom, y: (point.y - viewport.y) * viewport.zoom };
}

/** Pan by a distance in screen pixels: content follows the pointer. */
export function panBy(viewport: Viewport, dx: number, dy: number): Viewport {
  return { ...viewport, x: viewport.x - dx / viewport.zoom, y: viewport.y - dy / viewport.zoom };
}

/**
 * Zoom by a factor, keeping the scene point under `about` (screen pixels)
 * fixed, so zooming at the pointer zooms into what the pointer is over.
 */
export function zoomAbout(viewport: Viewport, factor: number, about: Point): Viewport {
  const zoom = clampZoom(viewport.zoom * factor);
  const anchor = screenToScene(viewport, about);
  return { x: anchor.x - about.x / zoom, y: anchor.y - about.y / zoom, zoom };
}

/** Frame the bounds in a canvas of the given size, never zooming past 100%. */
export function fitToBounds(bounds: Bounds, width: number, height: number, margin = 48): Viewport {
  const contentWidth = bounds.maxX - bounds.minX;
  const contentHeight = bounds.maxY - bounds.minY;
  if (contentWidth <= 0 && contentHeight <= 0) {
    // Nothing to frame: put the origin in the middle at 100%.
    return { x: -width / 2, y: -height / 2, zoom: 1 };
  }
  const zoom = clampZoom(
    Math.min(1, (width - 2 * margin) / Math.max(contentWidth, 1), (height - 2 * margin) / Math.max(contentHeight, 1)),
  );
  return {
    x: (bounds.minX + bounds.maxX) / 2 - width / (2 * zoom),
    y: (bounds.minY + bounds.maxY) / 2 - height / (2 * zoom),
    zoom,
  };
}

/**
 * The action for placing a template from the palette without a pointer
 * (requirement 12.7): with one line selected, into that line at its midpoint
 * — the keyboard equivalent of dropping onto it — otherwise at the centre of
 * the view.
 */
export function placeFromPalette(
  state: { selection: ReadonlySet<string>; viewport: Viewport },
  scene: { edges: readonly { ref: string; labelPosition: Point }[] },
  templateVersionId: string,
  width: number,
  height: number,
): { type: "PLACE_NODE"; templateVersionId: string; at: Point; intoEdge?: string } {
  const selected = state.selection.size === 1 ? [...state.selection][0] : undefined;
  const line = selected ? scene.edges.find((edge) => edge.ref === selected) : undefined;
  return line
    ? { type: "PLACE_NODE", templateVersionId, at: line.labelPosition, intoEdge: line.ref }
    : { type: "PLACE_NODE", templateVersionId, at: viewportCentre(state.viewport, width, height) };
}

/** The scene point at the centre of the canvas: where keyboard placement lands. */
export function viewportCentre(viewport: Viewport, width: number, height: number): Point {
  return screenToScene(viewport, { x: width / 2, y: height / 2 });
}
